<template>
  <v-card
    outlined
    class="mb-4">
    <div
      class="px-4 py-2 d-flex align-center"
      :style="{ background: mode === 'tentative' ? '#1976D2' : '#3949AB' }">
      <v-icon
        dark
        small
        class="mr-2">
        {{ mode === 'tentative' ? 'mdi-progress-clock' : 'mdi-factory' }}
      </v-icon>
      <span class="white--text text-body-2 font-weight-bold">
        {{ mode === 'tentative' ? 'มุมมอง : สินค้าตัวอย่าง' : 'มุมมอง : สินค้าสำหรับขาย' }}
      </span>
      <span
        class="white--text text-caption ml-2"
        style="opacity: 0.85;">
        {{ mode === 'tentative' ? '— pinned to order-stage month' : '— pinned to PO due-date month' }}
      </span>
    </div>

    <v-card-title
      class="d-flex align-center pb-2"
      style="gap: 8px;">
      <span class="text-subtitle-1 font-weight-medium">Utilization by factory</span>
    </v-card-title>

    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      height="2" />

    <div style="overflow-x: auto;">
      <table style="border-collapse: separate; border-spacing: 4px; min-width: 900px; width: 100%; padding: 0 16px 8px;">
        <thead>
          <tr>
            <th style="min-width: 160px; text-align: left; font-size: 12px; color: rgba(0,0,0,0.5); padding-bottom: 4px;">
              Factory
            </th>
            <th
              v-for="m in months"
              :key="`${m.year}-${m.month}`"
              style="min-width: 72px; text-align: center; font-size: 11px; padding-bottom: 4px;"
              :class="m.isToday ? 'primary--text font-weight-bold' : 'grey--text'">
              {{ m.label }}
            </th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="factory in utilization"
            :key="factory.factoryId">
            <td style="padding-right: 8px;">
              <span
                :class="['text-body-2', 'primary--text', { 'font-weight-bold': factory.factoryId === selectedFactoryId }]"
                style="cursor: pointer; white-space: nowrap;"
                @click="$emit('factory-click', factory.factoryId)">
                {{ factory.factoryName }}
                <v-icon
                  v-if="factory.factoryId === selectedFactoryId"
                  x-small
                  color="primary"
                  class="mr-1">
                  mdi-filter
                </v-icon>
                <v-icon
                  v-else
                  x-small
                  color="primary">
                  mdi-arrow-right
                </v-icon>
              </span>
            </td>
            <td
              v-for="m in months"
              :key="`${m.year}-${m.month}`"
              style="padding: 2px;">
              <heatmap-cell
                :metrics="getCellMetrics(factory, m.year, m.month)"
                :mode="mode"
                @click="$emit('factory-click', factory.factoryId)" />
            </td>
          </tr>

          <tr v-if="!loading && utilization.length === 0">
            <td
              :colspan="months.length + 1"
              class="text-center grey--text py-8">
              <v-icon
                large
                color="grey lighten-1">
                mdi-information-outline
              </v-icon>
              <div class="mt-2">
                No factories found
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <v-divider />
    <div
      class="d-flex flex-wrap align-center px-4 py-2"
      style="gap: 14px;">
      <span class="text-caption grey--text text--darken-1 font-weight-medium">Legend</span>
      <div
        v-for="item in legend"
        :key="item.label"
        class="d-flex align-center"
        style="gap: 5px;">
        <div :style="{ width: '12px', height: '12px', borderRadius: '3px', background: item.color, flexShrink: 0 }" />
        <span class="text-caption grey--text text--darken-2">{{ item.label }}</span>
      </div>
    </div>
  </v-card>
</template>

<script>
import HeatmapCell from './HeatmapCell.vue'

export default {
  name: 'UtilizationHeatmap',
  components: { HeatmapCell },
  props: {
    utilization: { type: Array, default: () => [] },
    mode: { type: String, default: 'tentative' },
    loading: { type: Boolean, default: false },
    windowStart: {
      type: Object,
      default: () => {
        const n = new Date()
        return { year: n.getFullYear(), month: n.getMonth() + 1 }
      }
    },
    windowEnd: {
      type: Object,
      default: () => {
        const n = new Date()
        const e = new Date(n.getFullYear(), n.getMonth() + 11, 1)
        return { year: e.getFullYear(), month: e.getMonth() + 1 }
      }
    },
    selectedFactoryId: { type: Number, default: null }
  },
  data () {
    return {
      legend: [
        { label: 'Under 50% (at risk)', color: '#FFCDD2' },
        { label: '50–70%', color: '#FFF9C4' },
        { label: '70–95% (healthy)', color: '#C8E6C9' },
        { label: '95–100%', color: '#FFE0B2' },
        { label: 'Over 100% (overloaded)', color: '#EF9A9A' }
      ]
    }
  },
  computed: {
    months () {
      const now = new Date()
      const result = []
      const end = new Date(this.windowEnd.year, this.windowEnd.month - 1, 1)
      const d = new Date(this.windowStart.year, this.windowStart.month - 1, 1)
      while (d <= end) {
        result.push({
          year: d.getFullYear(),
          month: d.getMonth() + 1,
          label: d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
          isToday: d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
        })
        d.setMonth(d.getMonth() + 1)
      }
      return result
    }
  },
  methods: {
    getCellMetrics (factory, year, month) {
      if (!factory.months) return null
      const entry = factory.months.find((m) => m.year === year && m.month === month)
      if (!entry) return null
      return this.mode === 'tentative' ? entry.tentative : entry.producing
    }
  }
}
</script>
